/** Fire the DNS probes that the test observes. Each fetch to a never-before-seen
 *  hostname forces the browser's resolver to query our authoritative nameserver;
 *  the collector sees which resolver egress IP asked. The HTTP request itself is
 *  expected to fail (NODATA / ERR_NAME_NOT_RESOLVED) — the DNS lookup is the payload. */

function nonce(): string {
  const b = crypto.getRandomValues(new Uint8Array(5));
  return [...b].map((x) => "abcdefghijklmnopqrstuvwxyz234567"[x & 31]).join("");
}

/** `<seq>-<nonce>.<testId>[.<region>].q.<domain>` — the region label pins the
 *  lookup to one of our nameserver locations (its own subzone). */
export function probeHost(seq: number, testId: string, probeDomain: string, region?: string): string {
  return `${seq}-${nonce()}.${testId}.${region ? `${region}.` : ""}q.${probeDomain}`;
}

export type ProbeOutcome = "sent" | "timeout" | "error";

/** AbortSignal.timeout is missing in older Safari/Firefox; fall back. */
function timeoutSignal(ms: number): AbortSignal | undefined {
  try {
    if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") return AbortSignal.timeout(ms);
    if (typeof AbortController !== "undefined") {
      const c = new AbortController();
      setTimeout(() => c.abort(), ms);
      return c.signal;
    }
  } catch { /* fall through */ }
  return undefined;
}

/** Issue one probe. Resolves with what happened to the *request* — the HTTP
 *  failure itself is expected (NODATA / ERR_NAME_NOT_RESOLVED): "sent" means the
 *  browser attempted the lookup, "error" means it never did. Never throws. */
export function fireProbe(seq: number, testId: string, probeDomain: string, region?: string): Promise<ProbeOutcome> {
  const host = probeHost(seq, testId, probeDomain, region);
  try {
    return fetch(`https://${host}/`, {
      // NOTE: never add redirect: "manual" here — the Fetch spec forbids it with
      // mode: "no-cors"; fetch() rejects with a TypeError *before sending*, so
      // no DNS lookup ever happens and the test silently sees zero resolvers.
      mode: "no-cors",
      cache: "no-store",
      signal: timeoutSignal(3000),
    }).then(
      () => "sent" as const,
      (e: unknown) => ((e as { name?: string })?.name === "AbortError" ? "timeout" : "sent"),
    );
  } catch (e) {
    console.warn("[dnsleak] probe could not be issued:", e);
    return Promise.resolve("error");
  }
}
